import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit2, Trash2, ShieldCheck, Lock, Save, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Roles = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = hasRole(user, 'super_admin');
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [permsDraft, setPermsDraft] = useState(new Set());
  const [savingPerms, setSavingPerms] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ label: '', key: '', description: '' });

  const load = useCallback(async () => {
    const r = await api.get('/roles');
    setRoles(r.data);
    if (!selectedRoleId && r.data.length > 0) {
      setSelectedRoleId(r.data[0].id);
      setPermsDraft(new Set(r.data[0].permissions));
    }
  }, [selectedRoleId]);

  const loadCatalog = useCallback(async () => {
    if (!isSuperAdmin) return;
    const r = await api.get('/permissions/catalog');
    setCatalog(r.data.modules || []);
  }, [isSuperAdmin]);

  useEffect(() => { load(); loadCatalog(); }, [load, loadCatalog]);

  const selectRole = (r) => {
    setSelectedRoleId(r.id);
    setPermsDraft(new Set(r.permissions || []));
  };

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  // Super Admin always has full access regardless of stored permissions.
  // Derive the effective permission set from the catalog so the UI reflects
  // newly added modules (equipment_location, equipment_photos, consumables,
  // purchase_requests, ?) without requiring a backend re-seed.
  const catalogPerms = useMemo(() => {
    const set = new Set();
    catalog.forEach((mod) => mod.actions.forEach((a) => set.add(`${mod.module}:${a}`)));
    return set;
  }, [catalog]);

  const togglePerm = (key) => {
    if (!isSuperAdmin || selectedRole?.key === 'super_admin') return;
    const next = new Set(permsDraft);
    next.has(key) ? next.delete(key) : next.add(key);
    setPermsDraft(next);
  };

  const toggleModuleAll = (moduleKey, actions) => {
    if (!isSuperAdmin || selectedRole?.key === 'super_admin') return;
    const keys = actions.map((a) => `${moduleKey}:${a}`);
    const allSelected = keys.every((k) => permsDraft.has(k));
    const next = new Set(permsDraft);
    keys.forEach((k) => allSelected ? next.delete(k) : next.add(k));
    setPermsDraft(next);
  };

  const savePerms = async () => {
    if (!selectedRole) return;
    setSavingPerms(true);
    try {
      await api.put(`/roles/${selectedRole.id}`, { permissions: Array.from(permsDraft) });
      toast({ title: 'Permissions updated', description: `${selectedRole.label} now has ${permsDraft.size} permission(s).` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSavingPerms(false); }
  };

  const createRole = async () => {
    if (!form.label.trim()) { toast({ title: 'Role name is required', variant: 'destructive' }); return; }
    try {
      await api.post('/roles', { label: form.label, key: form.key, description: form.description, permissions: [] });
      toast({ title: 'Role created' });
      setAddOpen(false);
      setForm({ label: '', key: '', description: '' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const removeRole = async (r) => {
    if (r.is_builtin) { toast({ title: 'Cannot delete built-in role', variant: 'destructive' }); return; }
    if (!window.confirm(`Delete role "${r.label}"? Users with this role will be moved to Requestor.`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast({ title: 'Role deleted' });
      if (selectedRoleId === r.id) setSelectedRoleId(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' });
    }
  };

  if (!isSuperAdmin) {
    return <div className="p-4 text-sm text-slate-600">Only Super Admin can manage roles & permissions.</div>;
  }

  const isSuperRole = selectedRole?.key === 'super_admin';
  const effectivePerms = isSuperRole ? catalogPerms : permsDraft;

  const dirty = selectedRole && !isSuperRole && (
    permsDraft.size !== (selectedRole.permissions?.length || 0) ||
    Array.from(permsDraft).some((k) => !selectedRole.permissions.includes(k))
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
      {/* Role list */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-3 border-b">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><ShieldCheck className="h-4 w-4" /> Roles</div>
            <Button size="sm" onClick={() => setAddOpen(true)} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)] h-8"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          <ul className="divide-y">
            {roles.map((r) => (
              <li key={r.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => selectRole(r)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectRole(r); }}
                  className={`w-full text-left p-3 hover:bg-slate-50 cursor-pointer ${selectedRoleId === r.id ? 'bg-blue-50/60 border-l-2 border-[hsl(217,91%,24%)]' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-sm text-slate-900 truncate">{r.label}</span>
                        {r.is_builtin && <Lock className="h-3 w-3 text-slate-400" title="Built-in role" />}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">{r.key} · {r.key === 'super_admin' ? catalogPerms.size : (r.permissions?.length || 0)} perms</div>
                    </div>
                    {!r.is_builtin && (
                      <button onClick={(e) => { e.stopPropagation(); removeRole(r); }} className="text-slate-400 hover:text-red-600 ml-2"><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Permission matrix */}
      <Card>
        <CardContent className="p-0">
          {!selectedRole ? (
            <div className="p-12 text-center text-slate-500 text-sm">Select a role to manage its permissions</div>
          ) : (
            <>
              <div className="p-4 border-b flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">{selectedRole.label}</h3>
                    {selectedRole.is_builtin && <Badge variant="secondary" className="text-[10px]">Built-in</Badge>}
                    {isSuperRole && <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px]">Locked</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{selectedRole.description || 'No description'}</p>
                  <p className="text-[11px] text-slate-400 font-mono mt-1">key: {selectedRole.key}</p>
                </div>
                {!isSuperRole && (
                  <Button onClick={savePerms} disabled={!dirty || savingPerms} className="bg-emerald-600 hover:bg-emerald-700">
                    {savingPerms ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-1.5" /> Save Permissions {dirty && <span className="ml-1 text-amber-200">—</span>}</>}
                  </Button>
                )}
              </div>
              {isSuperRole && (
                <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
                  The Super Admin role always has full access and cannot be edited.
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b text-left text-slate-600">
                    <tr>
                      <th className="p-3 w-64">Module</th>
                      <th className="p-3">Permissions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {catalog.map((mod) => {
                      const allSelected = mod.actions.every((a) => effectivePerms.has(`${mod.module}:${a}`));
                      const someSelected = mod.actions.some((a) => effectivePerms.has(`${mod.module}:${a}`));
                      return (
                        <tr key={mod.module}>
                          <td className="p-3 align-top">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={() => toggleModuleAll(mod.module, mod.actions)}
                                disabled={isSuperRole}
                                className={someSelected && !allSelected ? 'data-[state=checked]:bg-blue-500' : ''}
                              />
                              <div>
                                <div className="font-medium text-slate-900">{mod.label}</div>
                                <div className="text-[11px] text-slate-400 font-mono">{mod.module}</div>
                              </div>
                            </label>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1.5">
                              {mod.actions.map((a) => {
                                const key = `${mod.module}:${a}`;
                                const checked = effectivePerms.has(key);
                                return (
                                  <label key={a} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs cursor-pointer transition-colors ${checked ? 'bg-[hsl(217,91%,24%)] text-white border-[hsl(217,91%,24%)]' : 'bg-white border-slate-200 hover:border-slate-400'} ${isSuperRole ? 'opacity-60 cursor-not-allowed' : ''}`}>
                                    <Checkbox checked={checked} onCheckedChange={() => togglePerm(key)} disabled={isSuperRole} className="h-3.5 w-3.5" />
                                    {a.replace(/_/g, ' ')}
                                  </label>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Role</DialogTitle>
            <DialogDescription>Create a new role with custom permissions. You can configure access after creating it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Role Name *</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '_') })} placeholder="e.g. Finance Manager" /></div>
            <div><Label>Key (auto-generated)</Label><Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="finance_manager" className="font-mono" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={createRole} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">Create Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Roles;
